import { format } from 'date-fns';
import { pl } from 'date-fns/locale';
import { IEvent } from '../../../utils/types';
import { Title, TextField, Icon } from '../../common';
import { useTranslation } from '../../../hooks/translations/TranslationProvider';
import { COLOURS, LANGUAGES } from '../../../utils/consts';
import { useAppSelector } from '../../../store/hooks';

import './eventDetails.css';
import { Link } from 'react-router-dom';

interface EventDetailsInfoProps {
    eventDetails: IEvent;
}

const EventDetailsInfo = ({ eventDetails }: EventDetailsInfoProps) => {
    const { t } = useTranslation();
    const auth = useAppSelector(state => state.auth);
    const language = auth.authData?.language;
    const locale = language === LANGUAGES.PL ? pl : undefined;

    const eventPrice =
        (eventDetails.totalPrice / eventDetails.maxUsers) % 1 !== 0
            ? (eventDetails.totalPrice / eventDetails.maxUsers).toFixed(1)
            : eventDetails.totalPrice / eventDetails.maxUsers;

    const renderEventPrice =
        eventPrice === 0
            ? t('common.free')
            : `${eventPrice} ${eventDetails.currency} ${t('common.perPerson')}`;

    const eventDate = format(
        new Date(eventDetails.date),
        "eeee, dd MMM yyyy 'at' HH:mm",
        { locale }
    );

    const joinedUsers =
        eventDetails.teams[0].users.length + eventDetails.teams[1].users.length;

    const showLocationSection = eventDetails.address || eventDetails.mapsUrl;

    return (
        <div className="event-details">
            <Title text="Event Details" />
            <div className="event-details-section">
                <div className="icon-wrapper green">
                    <Icon
                        name="bx-calendar"
                        size="24px"
                        color={COLOURS.GREEN}
                    />
                </div>
                <div className="event-details-section-text">
                    <TextField weight="bold" color="subtitle">
                        {t('common.dateAndTime')}
                    </TextField>
                    <TextField>{eventDate}</TextField>
                </div>
            </div>
            <div className="event-details-section">
                <div className="icon-wrapper green">
                    <Icon name="bx-time" size="24px" color={COLOURS.GREEN} />
                </div>
                <div className="event-details-section-text">
                    <TextField weight="bold" color="subtitle">
                        {t('common.duration')}
                    </TextField>
                    <TextField>{`${eventDetails.duration} ${t(
                        'common.minutes'
                    )}`}</TextField>
                </div>
            </div>
            <div className="event-details-section">
                <div className="icon-wrapper green">
                    <Icon name="bx-group" size="24px" color={COLOURS.GREEN} />
                </div>
                <div className="event-details-section-text">
                    <TextField weight="bold" color="subtitle">
                        {t('common.totalPlayers')}
                    </TextField>
                    <TextField>{`${joinedUsers}/${eventDetails.maxUsers}`}</TextField>
                    <div className="badge badge-square badge-small badge-green">
                        <TextField size="s" color="green">
                            {eventDetails.isPrivate
                                ? t('common.confirmPlayers')
                                : t('common.openEvent')}
                        </TextField>
                    </div>
                </div>
            </div>
            {showLocationSection && (
                <div className="event-details-section">
                    <div className="icon-wrapper green">
                        <Icon name="bx-map" size="24px" color={COLOURS.GREEN} />
                    </div>
                    <div className="event-details-section-text">
                        <TextField weight="bold" color="subtitle">
                            {t('common.location')}
                        </TextField>
                        {eventDetails.address && (
                            <TextField>{eventDetails.address}</TextField>
                        )}
                        {eventDetails.mapsUrl && (
                            <Link to={eventDetails.mapsUrl} target="blank">
                                <div className="badge badge-square badge-small badge-green">
                                    <TextField size="s" color="green">
                                        Show on Maps
                                    </TextField>
                                </div>
                            </Link>
                        )}
                    </div>
                </div>
            )}
            <div className="event-details-section">
                <div className="icon-wrapper green">
                    <Icon name="bx-euro" size="24px" color={COLOURS.GREEN} />
                </div>
                <div className="event-details-section-text">
                    <TextField weight="bold" color="subtitle">
                        {t('common.price')}
                    </TextField>
                    <TextField>{renderEventPrice}</TextField>
                </div>
            </div>
            {eventDetails.ownerNotes && (
                <div className="event-details-section">
                    <div className="icon-wrapper green">
                        <Icon
                            name="bx-info-circle"
                            size="24px"
                            color={COLOURS.GREEN}
                        />
                    </div>
                    <div className="event-details-section-text">
                        <TextField weight="bold" color="subtitle">
                            {t('common.notes')}
                        </TextField>
                        <TextField>{eventDetails.ownerNotes}</TextField>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EventDetailsInfo;
